import { FC, PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { findWorkspaceInviteQueryDocument } from "@/domain/workspace/hooks/api/workspace-membership.gql";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { useQuery } from "@tanstack/react-query";

import { AcceptWorkspaceInviteCard } from "../components/accept-workspace-invite-card";

export function AcceptInviteWorkspacePage() {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("invite.acceptInvite"));
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");

  if (!token) {
    return <NotFoundView />;
  }

  return <IsValidTokenInnerPage token={token} />;
}

export type UserProp = {
  id: string;
  email: string;
  fullname?: string | null | undefined;
  username?: string | null | undefined;
  profilePicture?: string | null | undefined;
  activated: boolean;
  hasSetPassword: boolean;
};

export type FederatedLoginProp = {
  startUrl: string | null;
};

export interface IsValidTokenInnerPageProps extends PropsWithChildren {
  token: string;
}

const IsValidTokenInnerPage: FC<IsValidTokenInnerPageProps> = ({ token }) => {
  const { data, isLoading: loading } = useQuery({
    queryKey: ["findWorkspaceInvite", token],
    queryFn: async () => {
      const client = createOperationClient("findWorkspaceInvite");
      return await client.request(findWorkspaceInviteQueryDocument, { input: token });
    },
  });
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [user, setUser] = useState<UserProp | null>(null);
  const [federatedLogin, setFederatedLogin] = useState<FederatedLoginProp | null>(null);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (!data.findWorkspaceInvite.user) {
      return;
    }
    if (!data.findWorkspaceInvite.workspace) {
      return;
    }

    setWorkspace({
      ...(data.findWorkspaceInvite.workspace as Workspace),
    });
    setUser({
      ...data.findWorkspaceInvite.user,
    });
    setFederatedLogin(
      data.findWorkspaceInvite.federatedLogin
        ? { startUrl: data.findWorkspaceInvite.federatedLogin.startUrl ?? null }
        : null,
    );
  }, [data]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
        <LoadingDots color="#808080" />
      </div>
    );
  }

  if (!user || !workspace) {
    return <NotFoundView />;
  }

  return <InnerPage token={token} workspace={workspace} user={user} federatedLogin={federatedLogin} />;
};

export interface InnerPageProps extends PropsWithChildren {
  token: string;
  workspace: Workspace;
  user: UserProp;
  federatedLogin: FederatedLoginProp | null;
}

const InnerPage: FC<InnerPageProps> = ({ token, workspace, user, federatedLogin }) => {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background text-foreground">
      <AcceptWorkspaceInviteCard token={token} workspace={workspace} user={user} federatedLogin={federatedLogin} />
    </div>
  );
};
